<i18n src="./resources/locales.json"></i18n>

<template>
  <v-card
    ref="agentStatsSummaryCard"
    class="agent-stats-summary-card elevation-3"
    :style="CSS_VARS"
    @mouseenter.stop="keepOpen"
    @mouseleave.stop="syncClose"
  >
    <section v-if="showSummary" class="agent-summary" :dark="$vuetify.theme.dark">
      <v-expansion-panels v-model="panels" flat accordion multiple>
        <v-expansion-panel class="exp-panel">
          <v-expansion-panel-header>
            <div class="current-agent-container">
              <EcsAvatar
                :contact-id="agentStats.agentId"
                :alt="agentStats.agentName"
                :size="40"
                stateful
                no-animation
              />
              <div class="agent-info">
                <div class="agent-name">
                  {{ agentStats.agentName }} <span class="agent-state"> &bullet; {{ currentStatus.label }}</span>
                </div>
                <div class="active-interactions-counter">
                  <v-progress-circular
                    v-if="isLoading"
                    class="loading"
                    size="16"
                    width="2"
                    color="primary"
                    indeterminate
                  />
                  <span v-else class="counter">{{ agentInteractions.length }}</span>
                  {{ $t('graph.interaction.active') }}
                </div>
              </div>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <InteractionsGraph :interactions="agentInteractions" :agent-id="agentStats.agentId" />
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel class="exp-panel">
          <v-expansion-panel-header>
            <div class="interaction-summary-section">
              <div v-once class="section-header">
                {{ $t('interactions') }}
              </div>
              <div class="collapsed-resume">
                <div>
                  <span class="counter">{{ agentStats.totalAnsweredInteractions }}</span>
                  {{ $t('graph.interaction.answered') }}
                </div>
                <div>
                  <span class="counter">{{ agentStats.totalMissedInteractions }}</span>
                  {{ $t('graph.interaction.missed') }}
                </div>
                <div>
                  <span class="counter">{{ agentStats.totalRejectedInteractions }}</span>
                  {{ $t('graph.interaction.rejected') }}
                </div>
                <div>
                  <span class="counter">{{ agentStats.totalTransferredInteractions }}</span>
                  {{ $t('graph.interaction.transferred') }}
                </div>
              </div>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="interactions-summary">
              <table>
                <tr>
                  <td></td>
                  <td><v-icon class="ml-3 mb-2" small> mdi-phone </v-icon></td>
                  <td><v-icon class="ml-3 mb-2" small> mdi-message-text </v-icon></td>
                  <td><v-icon class="ml-3 mb-2" small> mdi-email </v-icon></td>
                  <td><v-icon class="ml-3 mb-2" small> mdi-chat-processing-outline </v-icon></td>
                  <td></td>
                </tr>
                <tr>
                  <td v-once>{{ $t('graph.interaction.answered') }}</td>
                  <td class="count">{{ agentStats.answeredInteractions.telephony || 0 }}</td>
                  <td class="count">{{ agentStats.answeredInteractions.chat || 0 }}</td>
                  <td class="count">{{ agentStats.answeredInteractions.email || 0 }}</td>
                  <td class="count">{{ agentStats.answeredInteractions.sms || 0 }}</td>
                  <td class="total-count">{{ agentStats.totalAnsweredInteractions || 0 }}</td>
                  <td></td>
                </tr>
                <tr>
                  <td v-once>{{ $t('graph.interaction.missed') }}</td>
                  <td class="count">{{ agentStats.missedInteractions.telephony || 0 }}</td>
                  <td class="count">{{ agentStats.missedInteractions.chat || 0 }}</td>
                  <td class="count">{{ agentStats.missedInteractions.email || 0 }}</td>
                  <td class="count">{{ agentStats.missedInteractions.sms || 0 }}</td>
                  <td class="total-count">{{ agentStats.totalMissedInteractions }}</td>
                </tr>
                <tr>
                  <td v-once>{{ $t('graph.interaction.rejected') }}</td>
                  <td class="count">{{ agentStats.rejectedInteractions.telephony || 0 }}</td>
                  <td class="count">{{ agentStats.rejectedInteractions.chat || 0 }}</td>
                  <td class="count">{{ agentStats.rejectedInteractions.email || 0 }}</td>
                  <td class="count">{{ agentStats.rejectedInteractions.sms || 0 }}</td>
                  <td class="total-count">{{ agentStats.totalRejectedInteractions || 0 }}</td>
                </tr>
                <tr>
                  <td v-once>{{ $t('graph.interaction.transferred') }}</td>
                  <td class="count">{{ agentStats.transferredInteractions.telephony || 0 }}</td>
                  <td class="count">{{ agentStats.transferredInteractions.chat || 0 }}</td>
                  <td class="count">{{ agentStats.transferredInteractions.email || 0 }}</td>
                  <td class="count">{{ agentStats.transferredInteractions.sms || 0 }}</td>
                  <td class="total-count">{{ agentStats.totalTransferredInteractions || 0 }}</td>
                </tr>
                <tr>
                  <td colspan="5">
                    <div class="avg-handling-time">
                      {{ $t('graph.interaction.avgHandling') }}
                      <span class="time">
                        {{ kpiFormat(agentStats.agentAverageTreatingTime.value, 'duration', true) }}
                      </span>
                    </div>
                  </td>
                </tr>
              </table>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel class="exp-panel">
          <v-expansion-panel-header>
            <div>
              <div v-once class="section-header">
                {{ $t('agentTime') }}
              </div>
              <div class="collapsed-resume mb-1">
                <div v-for="(item, index) in timeDistributionDataset" :key="index">
                  <span class="counter"> {{ item.legend.percent }} </span>
                  <span> {{ item.legend.label }} </span>
                </div>
              </div>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <section class="agent-timing">
              <div class="graph-container">
                <EcsDonutGraph :dataset="timeDistributionDataset" :dark="$vuetify.theme.dark" />
                <div class="login-time-span">
                  {{ $t('loginTimeSpan') }}
                  <span class="time">{{ agentTiming.loginTimeSpan.duration }}</span>
                </div>
              </div>
            </section>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </section>
  </v-card>
</template>

<script>
import EcsAvatar from '@/components/ecs-base-interaction-components/ecs-avatar/EcsAvatar';
import InteractionService from '@/services/interactionService';
import EcsInteraction from '@/components/ecs-interaction/EcsInteraction';
import EcsKpiFormatter from '@/mixins/ecs-kpi-formatter/EcsKpiFormatter';
import EcsAgentStatsMath from '@/components/ecs-agent-stats/EcsAgentStats.math';
import EcsDonutGraph from '@/components/ecs-graphs/ecs-donut-graph/EcsDonutGraph';
import InteractionsGraph from '@/components/ecs-agent-info/interactions-graph/InteractionsGraph';
import { AGENT_STATE } from '@/constants/agent/constants';

export default {
  name: 'EcsAgentStatsSummaryCardSingleton',

  components: { InteractionsGraph, EcsAvatar, EcsDonutGraph },

  mixins: [EcsKpiFormatter],

  data() {
    return {
      show: false,
      position: {
        x: null,
        y: null
      },
      finalPosition: {
        type: Object,
        default() {
          return {};
        }
      },
      borderGap: 16,
      closeTimeoutRef: null,
      agentStats: {},
      agentInteractions: [],
      isLoading: false,
      currentStatus: '',
      panels: [0]
    };
  },

  computed: {
    CSS_VARS() {
      return {
        '--top': `${this.finalPosition.y}px`,
        '--left': `${this.finalPosition.x}px`,
        '--display': this.show ? 'block' : 'none'
      };
    },

    agentTiming() {
      return EcsAgentStatsMath.agentTimingSecs(this.agentStats);
    },

    timeDistributionDataset() {
      return [
        {
          color: '#4ea96d',
          value: this.agentTiming.readyTime.value,
          legend: {
            label: this.$t('graph.legend.readyTime'),
            value: this.agentTiming.readyTime.duration,
            // eslint-disable-next-line prettier/prettier
            percent: `${((this.agentTiming.readyTime.value * 100) / this.agentTiming.loginTimeSpan.value || 0).toFixed(0)}%`
          }
        },
        {
          color: '#f44336',
          value: this.agentTiming.busyTime.value,
          legend: {
            label: this.$t('graph.legend.busyTime'),
            value: this.agentTiming.busyTime.duration,
            // eslint-disable-next-line prettier/prettier
            percent: `${((this.agentTiming.busyTime.value * 100) / this.agentTiming.loginTimeSpan.value || 0).toFixed(0)}%`
          }
        },
        {
          color: '#ffc107',
          value: this.agentTiming.breakTime.value,
          legend: {
            label: this.$t('graph.legend.breakTime'),
            value: this.agentTiming.breakTime.duration,
            // eslint-disable-next-line prettier/prettier
            percent: `${((this.agentTiming.breakTime.value * 100) / this.agentTiming.loginTimeSpan.value || 0).toFixed(0)}%`
          }
        },
        {
          color: 'var(--v-xtium-robin-egg-blue)',
          value: this.agentTiming.backofficeTime.value,
          legend: {
            label: this.$t('graph.legend.backofficeTime'),
            value: this.agentTiming.backofficeTime.duration,
            percent:
              // eslint-disable-next-line prettier/prettier
              `${((this.agentTiming.backofficeTime.value * 100) / this.agentTiming.loginTimeSpan.value || 0).toFixed(0)}%`
          }
        },
        {
          color: '#abcdef',
          value: this.agentTiming.outgoingTime.value,
          legend: {
            label: this.$t('graph.legend.outgoingTime'),
            value: this.agentTiming.outgoingTime.duration,
            percent:
              // eslint-disable-next-line prettier/prettier
              `${((this.agentTiming.outgoingTime.value * 100) / this.agentTiming.loginTimeSpan.value || 0).toFixed(0)}%`
          }
        }
      ];
    },

    showSummary() {
      return this.agentStats && this.show;
    }
  },

  created() {
    this.$root.$on('open-agent-stats-summary-card', this.open);
    this.$root.$on('close-agent-stats-summary-card', this.close);
  },

  beforeDestroy() {
    this.$root.$off('open-agent-stats-summary-card', this.open);
    this.$root.$off('close-agent-stats-summary-card', this.close);
  },

  methods: {
    keepOpen() {
      clearTimeout(this.closeTimeoutRef);
    },

    open({ agentStats, currentStatus, x, y }) {
      this.agentStats = agentStats;
      this.currentStatus = currentStatus;
      this.agentInteractions = [];
      this.position.x = x;
      this.position.y = y;
      this.panels = [0];
      this.show = true;
      this.calcMenuPosition();
      this.getAgentInteractions();
      clearTimeout(this.closeTimeoutRef);
    },

    syncClose() {
      clearTimeout(this.closeTimeoutRef);
      this.agentStats = {};
      this.show = false;
    },

    close() {
      this.closeTimeoutRef = setTimeout(() => {
        this.agentStats = {};
        this.show = false;
      }, 300);
    },

    getAgentInteractions() {
      if (this.currentStatus.id === AGENT_STATE.offline) {
        return;
      }

      this.isLoading = true;
      InteractionService.getAgentInteractions(this.agentStats.agentId)
        .then(({ data }) => {
          this.agentInteractions = data.map(interaction => new EcsInteraction(interaction));
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    async calcMenuPosition() {
      await this.$nextTick();

      const agentStatsSummaryCard = this.$refs.agentStatsSummaryCard.$el;
      let x = this.position.x;
      let y = this.position.y;

      if (!agentStatsSummaryCard) {
        return;
      }

      const availableWidth = document.body.clientWidth - this.borderGap;
      const availableHeight = document.body.clientHeight - this.borderGap;
      const cardWidth = agentStatsSummaryCard.getBoundingClientRect().width;
      const cardHeight = agentStatsSummaryCard.getBoundingClientRect().height;

      x = x + cardWidth > availableWidth ? availableWidth - cardWidth : x;
      y = y + cardHeight > availableHeight ? availableHeight - cardHeight : y;

      this.finalPosition = { x, y };
    }
  }
};
</script>

<style src="./scoped.scss" lang="scss" scoped />
